import { TIER_LABEL, moduleLabel, moduleShortLabel } from "../lib/riskVocabulary";
import { useState, useMemo, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type {
  AssetRiskResult,
  RiskLevel,
  ModuleCode,
  ParameterContribution,
} from "../api/types";
import { RiskBadge } from "../components/risk-badge";
import { Pagination } from "../components/pagination";
import {
  FOUNDATION_INPUT_MODULES,
  INTEGRATED_MODULES,
} from "../lib/module-inputs";

// =============================================================================
// Slice 6c: Run Results — tabular redesign.
// Slice 9.9: Run comparison view + CSV export.
//
// New in Slice 9.9:
//   - "Compare with previous run" picker above the table
//   - Per-asset delta column (▲▼ risk level change between two runs)
//   - "Export CSV" button — downloads the full results table as CSV
//   - Executive summary bar — assets per band of Overall risk (High or above / Moderate / Low)
// =============================================================================

const RISK_RANK: Record<RiskLevel, number> = {
  Critical: 7,
  Very_high: 6,
  High: 5,
  Moderate: 4,
  Low: 3,
  Very_low: 2,
  No_risk: 1,
  NA: 0,
};

interface RunRow extends AssetRiskResult {
  module_code: ModuleCode;
  /** Present on the lean payload (server-side JOIN on asset.name). */
  asset_name?: string;
  /** Present on the lean payload (server-side JOIN on asset_type.name). */
  asset_type_name?: string;
}

interface AssetGroup {
  asset_id: string;
  asset_name: string;
  asset_type_name: string;
  results: RunRow[];
  composite: RiskLevel | null;
  composite_score: number | null;
}

interface AssessmentRun {
  id: string;
  run_type: string;
  modules_run: string[];
  engine_version: string;
  started_at: string;
  completed_at: string | null;
  status: "Running" | "Completed" | "Failed";
  summary: { asset_count?: number } | null;
}

type SortKey = "asset_name" | "composite" | ModuleCode;

/** The composite the page headlines and ranks on: Overall risk, else Baseline risk (older runs). */
function compositeCodeFor(modules: readonly string[]): "integrated_advanced" | "integrated_foundation" | null {
  if (modules.includes("integrated_advanced")) return "integrated_advanced";
  if (modules.includes("integrated_foundation")) return "integrated_foundation";
  return null;
}
type SortDir = "asc" | "desc";

// =============================================================================
// Executive summary bar — counts of assets per risk band
// =============================================================================

function ExecutiveSummaryBar({
  groups,
  modulesInResults,
}: {
  groups: AssetGroup[];
  modulesInResults: ModuleCode[];
}) {
  // Headline on Overall risk (all seven dimensions; drives priority). Older
  // runs without it fall back to Baseline risk, then to the worst module.
  const headlineCode = compositeCodeFor(modulesInResults);
  const integratedPresent = headlineCode !== null;
  const riskCounts: Record<RiskLevel, number> = {
    Critical: 0,
    Very_high: 0,
    High: 0,
    Moderate: 0,
    Low: 0,
    Very_low: 0,
    No_risk: 0,
    NA: 0,
  };

  for (const g of groups) {
    if (integratedPresent && g.composite) {
      riskCounts[g.composite]++;
    } else {
      // Worst risk across all modules
      let worst: RiskLevel = "NA";
      for (const r of g.results) {
        if (RISK_RANK[r.risk_level] > RISK_RANK[worst]) worst = r.risk_level;
      }
      riskCounts[worst]++;
    }
  }

  const criticalHigh =
    riskCounts.Critical + riskCounts.Very_high + riskCounts.High;
  const moderate = riskCounts.Moderate;
  const low = riskCounts.Low + riskCounts.Very_low + riskCounts.No_risk;
  const na = riskCounts.NA;

  const bands = [
    {
      label: "High or above",
      count: criticalHigh,
      color: "bg-red-600 text-white",
      textColor: "text-red-700",
    },
    {
      label: "Moderate",
      count: moderate,
      color: "bg-amber-500 text-white",
      textColor: "text-amber-700",
    },
    {
      label: "Low / No risk",
      count: low,
      color: "bg-emerald-600 text-white",
      textColor: "text-emerald-700",
    },
    {
      label: "Not scored",
      count: na,
      color: "bg-ink-300 text-white",
      textColor: "text-ink-500",
    },
  ];

  return (
    <div className="mb-6 border border-ink-100 bg-white rounded-sm p-4">
      <div className="text-xs font-medium text-ink-500 uppercase tracking-wider mb-3">
        Executive summary —{" "}
        {headlineCode ? moduleLabel(headlineCode) : "Worst module"} ·{" "}
        {groups.length} {groups.length === 1 ? "asset" : "assets"}
      </div>
      <div className="flex gap-4 items-center flex-wrap">
        {bands.map((b) => (
          <div key={b.label} className="flex items-center gap-2">
            <span className={`text-xl font-bold ${b.textColor}`}>
              {b.count}
            </span>
            <span className="text-xs text-ink-500">{b.label}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex h-3 rounded-sm overflow-hidden bg-ink-100">
        {bands
          .filter((b) => b.count > 0)
          .map((b) => (
            <div
              key={b.label}
              className={b.color}
              style={{ width: `${(b.count / groups.length) * 100}%` }}
              title={`${b.label}: ${b.count}`}
            />
          ))}
      </div>
    </div>
  );
}

// =============================================================================
// Run comparison delta badge
// =============================================================================

function DeltaBadge({
  current,
  previous,
}: {
  current: RiskLevel | null;
  previous: RiskLevel | null;
}) {
  if (!current || !previous)
    return <span className="text-ink-300 text-[10px]">—</span>;
  const delta = RISK_RANK[current] - RISK_RANK[previous];
  if (delta === 0) return <span className="text-ink-400 text-[10px]">━</span>;
  if (delta > 0)
    return (
      <span
        className="text-red-600 text-[10px] font-semibold"
        title={`Was ${previous}`}
      >
        ▲ {current.replace("_", " ")}
      </span>
    );
  return (
    <span
      className="text-emerald-600 text-[10px] font-semibold"
      title={`Was ${previous}`}
    >
      ▼ {current.replace("_", " ")}
    </span>
  );
}

// =============================================================================
// CSV export
// =============================================================================

/**
 * Builds and downloads the results CSV without freezing the tab.
 *
 * The previous version did all of this synchronously in the click handler. At
 * 10k assets that is 10,000 x (2 + 2 x modules) cells, each with a nested
 * find() per module — quadratic in modules and entirely on the main thread, so
 * the tab stopped painting and the browser offered to kill it.
 *
 * Two changes: build a module_code -> row index per asset ONCE (removing the
 * nested find), and yield to the event loop between chunks so the UI keeps
 * responding and the caller can show progress.
 */
async function exportToCSV(
  groups: AssetGroup[],
  modulesInResults: ModuleCode[],
  runId: string,
  onProgress?: (done: number, total: number) => void,
) {
  const headers = [
    "Asset ID",
    "Asset Name",
    // Third, next to the name it qualifies. Safe here, unlike a fixed-width
    // export: the risk and score columns below are already one per module, so
    // nothing downstream can be indexing by position past the leading few.
    "Asset Type",
    ...modulesInResults.map((m) => `${moduleLabel(m)} (Risk)`),
    ...modulesInResults.map((m) => `${moduleLabel(m)} (Score)`),
  ];

  const esc = (cell: unknown) => `"${String(cell).replace(/"/g, '""')}"`;
  const lines: string[] = [headers.map(esc).join(",")];

  const CHUNK = 500;
  for (let i = 0; i < groups.length; i += CHUNK) {
    const slice = groups.slice(i, i + CHUNK);
    for (const g of slice) {
      // One pass over this asset's results instead of a find() per module.
      const byModule = new Map<string, RunRow>();
      for (const r of g.results) byModule.set(r.module_code, r);

      const riskCols = modulesInResults.map((m) => {
        const r = byModule.get(m);
        return r ? r.risk_level.replace("_", " ") : "N/A";
      });
      const scoreCols = modulesInResults.map((m) => {
        const r = byModule.get(m);
        return r ? Number(r.normalized_score).toFixed(1) : "";
      });
      lines.push([g.asset_id, g.asset_name, g.asset_type_name, ...riskCols, ...scoreCols].map(esc).join(","));
    }
    onProgress?.(Math.min(i + CHUNK, groups.length), groups.length);
    // Hand the main thread back so React can paint and input stays live.
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `qram-results-${runId.slice(0, 8)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// =============================================================================
// Main Results component
// =============================================================================

export function Results() {
  const { runId } = useParams<{ runId: string }>();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("composite");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [compareRunId, setCompareRunId] = useState<string | null>(null);
  // Render one page at a time. Previously every asset row was mounted, so a
  // 10k-asset run built ~10,000 rows x ~9 badge cells (~90,000 DOM nodes) in a
  // single commit — seconds of blocked main thread and the browser's
  // "not responding" prompt. Sorting/summary/CSV still span the whole run.
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  const resultsQ = useQuery({
    queryKey: ["run-results", runId],
    // lean=1: badges + asset_name only. The heavy per-module JSON
    // (parameter_contributions / module_outputs) is fetched per asset when a
    // row is expanded, instead of shipping ~90k JSON blobs up front.
    queryFn: () => api.get<RunRow[]>(`/runs/${runId}/results?lean=1`),
    enabled: !!runId,
  });

  // Fetch run metadata to get project ID → fetch run list for comparison picker
  const runQ = useQuery({
    queryKey: ["run", runId],
    queryFn: () =>
      api.get<{ id: string; project_id: string; started_at: string }>(
        `/runs/${runId}`,
      ),
    enabled: !!runId,
  });

  const projectId = runQ.data?.project_id;

  const allRunsQ = useQuery({
    queryKey: ["project-runs", projectId],
    queryFn: () => api.get<AssessmentRun[]>(`/projects/${projectId}/runs`),
    enabled: !!projectId,
  });

  // Fetch comparison run results when a comparison is selected
  const compareResultsQ = useQuery({
    queryKey: ["run-results", compareRunId],
    queryFn: () => api.get<RunRow[]>(`/runs/${compareRunId}/results?lean=1`),
    enabled: !!compareRunId,
  });

  const groups = useMemo<AssetGroup[]>(() => {
    const rows = resultsQ.data ?? [];
    const map = new Map<string, AssetGroup>();
    for (const r of rows) {
      let g = map.get(r.asset_id);
      if (!g) {
        g = {
          asset_id: r.asset_id,
          // The lean payload carries asset_name from a JOIN, so names arrive
          // with the results. The id fallback covers the non-lean shape.
          asset_name: r.asset_name ?? r.asset_id.slice(0, 8) + "…",
          asset_type_name: r.asset_type_name ?? "",
          results: [],
          composite: null,
          composite_score: null,
        };
        map.set(r.asset_id, g);
      }
      g.results.push(r);
    }
    const code = compositeCodeFor(rows.map((r) => r.module_code));
    for (const g of map.values()) {
      const c = g.results.find((r) => r.module_code === code);
      if (c) {
        g.composite = c.risk_level;
        g.composite_score = c.normalized_score == null ? null : Number(c.normalized_score);
      }
    }
    return Array.from(map.values());
  }, [resultsQ.data]);

  // Build a map from asset_id → composite risk for the comparison run
  const compareCompositeByAsset = useMemo<Map<string, RiskLevel>>(() => {
    const out = new Map<string, RiskLevel>();
    const rows = compareResultsQ.data ?? [];
    const code = compositeCodeFor(rows.map((r) => r.module_code));
    for (const r of rows) {
      if (r.module_code === code) out.set(r.asset_id, r.risk_level);
    }
    return out;
  }, [compareResultsQ.data]);

  // Asset names now come from the results payload itself.
  //
  // This previously issued ONE GET /assets/{id} per asset inside a
  // Promise.all — 10,000 parallel requests on a 10k-asset run. That saturated
  // the browser's connection pool, pegged the main thread on response
  // handling, and was the direct cause of the "page unresponsive" dialog (and
  // of names rendering as truncated UUIDs when the requests failed). The
  // server now joins asset.name into the lean result rows, so the same
  // information costs zero extra requests.
  const groupsWithNames = groups;

  const modulesInResults = useMemo<ModuleCode[]>(() => {
    const present = new Set<ModuleCode>();
    for (const r of resultsQ.data ?? []) present.add(r.module_code);
    const ordered: ModuleCode[] = [];
    for (const m of FOUNDATION_INPUT_MODULES)
      if (present.has(m)) ordered.push(m);
    for (const m of INTEGRATED_MODULES) if (present.has(m)) ordered.push(m);
    for (const m of present) {
      if (
        !FOUNDATION_INPUT_MODULES.includes(m) &&
        !INTEGRATED_MODULES.includes(m)
      ) {
        ordered.push(m);
      }
    }
    return ordered;
  }, [resultsQ.data]);

  // Step 2 (C-037): Overall risk sits in its own group, first; the Baseline
  // risk (business exposure) group holds its four dimensions and its
  // composite; the Technical exposure group holds Threat, Cryptography and
  // Compliance.
  const overallCols = modulesInResults.filter((m) => m === "integrated_advanced");
  const foundationCols = modulesInResults.filter((m) =>
    FOUNDATION_INPUT_MODULES.includes(m) || m === "integrated_foundation",
  );
  // UI consistency fix (2026-05-19): Threat groups under Advanced here,
  // matching the asset-grid convention. The previous layout broke "Threat"
  // out as its own group ("THREAT ANALYSIS") which was inconsistent with
  // the Assets page and confused users into thinking Threat was a separate
  // analysis stage rather than an Advanced module alongside Crypto/Compliance.
  // Threat is still ordered FIRST within the Advanced group so it remains
  // visually adjacent to Integrated — same column position as before.
  const advancedCols = modulesInResults.filter(
    (m) =>
      !FOUNDATION_INPUT_MODULES.includes(m) && !INTEGRATED_MODULES.includes(m),
  );
  // Put 'threat' first within advancedCols so column order matches the prior layout.
  advancedCols.sort((a, b) => {
    if (a === "threat") return -1;
    if (b === "threat") return 1;
    return 0;
  });

  const allModuleCols = [...overallCols, ...foundationCols, ...advancedCols];

  const sorted = useMemo(() => {
    const cmp = (a: AssetGroup, b: AssetGroup) => {
      if (sortKey === "asset_name") {
        const sa = a.asset_name.toLowerCase();
        const sb = b.asset_name.toLowerCase();
        return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
      }
      let ra: number;
      let rb: number;
      if (sortKey === "composite") {
        ra = a.composite ? RISK_RANK[a.composite] : -1;
        rb = b.composite ? RISK_RANK[b.composite] : -1;
        // Same level: rank on the composite score, so the order matches priority within a band.
        if (ra === rb) {
          const sa = a.composite_score ?? -1;
          const sb = b.composite_score ?? -1;
          return sortDir === "asc" ? sa - sb : sb - sa;
        }
      } else {
        const fa = a.results.find((r) => r.module_code === sortKey);
        const fb = b.results.find((r) => r.module_code === sortKey);
        ra = fa ? RISK_RANK[fa.risk_level] : -1;
        rb = fb ? RISK_RANK[fb.risk_level] : -1;
      }
      return sortDir === "asc" ? ra - rb : rb - ra;
    };
    return [...groupsWithNames].sort(cmp);
  }, [groupsWithNames, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    setPageIndex(0); // a new ordering means a new page 1
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // The slice actually mounted. Clamped so a page can never fall out of range
  // (e.g. after switching to a run with fewer assets).
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const pageRows = useMemo(
    () => sorted.slice(safePageIndex * pageSize, safePageIndex * pageSize + pageSize),
    [sorted, safePageIndex, pageSize],
  );

  const [exportProgress, setExportProgress] = useState<string | null>(null);
  const handleExportCSV = useCallback(async () => {
    if (!runId || exportProgress) return;
    try {
      setExportProgress("Preparing…");
      await exportToCSV(sorted, modulesInResults, runId, (done, total) => {
        setExportProgress(total > 1000 ? `${Math.round((done / total) * 100)}%` : "Preparing…");
      });
    } catch (err) {
      console.error("CSV export failed", err);
      setExportProgress(null);
      window.alert("The CSV export failed. Nothing was downloaded — please try again.");
      return;
    }
    setExportProgress(null);
  }, [sorted, modulesInResults, runId, exportProgress]);

  if (resultsQ.isLoading)
    return <p className="p-8 text-ink-500 text-sm">Loading results…</p>;
  if (resultsQ.error) {
    return (
      <p className="p-8 text-accent">
        Failed to load results: {(resultsQ.error as Error).message}
      </p>
    );
  }

  if (sorted.length === 0) {
    return (
      <div className="p-8">
        <Link
          to={`/projects/${projectId}`}
          className="text-xs text-ink-500 hover:text-ink-900"
        >
          ← Back to project
        </Link>
        <h1 className="font-display text-3xl text-ink-900 mt-4">Run results</h1>
        <p className="text-ink-500 mt-6">No results for this run.</p>
      </div>
    );
  }

  // Completed runs for comparison picker (exclude current run)
  const completedRuns = (allRunsQ.data ?? []).filter(
    (r) => r.status === "Completed" && r.id !== runId,
  );

  const hasComparison = !!compareRunId && compareResultsQ.data !== undefined;
  const colSpanTotal = 2 + allModuleCols.length + (hasComparison ? 1 : 0);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-6">
        <div className="mb-2">
          <Link
            to={`/projects/${projectId}`}
            className="text-xs text-ink-500 hover:text-ink-900"
          >
            ← Back to project
          </Link>
        </div>

        {/* Header row */}
        <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl text-ink-900 mb-1">
              Run results
            </h1>
            <p className="text-sm text-ink-500">
              {sorted.length} {sorted.length === 1 ? "asset" : "assets"} ·{" "}
              {modulesInResults.length}{" "}
              {modulesInResults.length === 1 ? "module" : "modules"} scored
              {runQ.data?.started_at && (
                <span className="ml-2 font-mono">
                  · {new Date(runQ.data.started_at).toLocaleString()}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Run comparison picker */}
            {completedRuns.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-ink-500 whitespace-nowrap">
                  Compare with:
                </label>
                <select
                  className="text-xs border border-ink-200 rounded-sm px-2 py-1 text-ink-700 focus:outline-none focus:border-accent"
                  value={compareRunId ?? ""}
                  onChange={(e) => setCompareRunId(e.target.value || null)}
                >
                  <option value="">— None —</option>
                  {completedRuns.map((r) => (
                    <option key={r.id} value={r.id}>
                      {new Date(r.started_at).toLocaleString()} (
                      {r.summary?.asset_count ?? "?"} assets)
                    </option>
                  ))}
                </select>
                {compareResultsQ.isLoading && (
                  <span className="text-xs text-ink-400">Loading…</span>
                )}
              </div>
            )}

            {/* Export CSV */}
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={!!exportProgress}
              className="text-xs px-3 py-1.5 border border-ink-200 rounded-sm text-ink-600 hover:border-accent hover:text-accent transition-colors disabled:opacity-60"
            >
              {exportProgress ? `Exporting ${exportProgress}` : "↓ Export CSV"}
            </button>
          </div>
        </div>

        {/* Executive summary */}
        <ExecutiveSummaryBar
          groups={sorted}
          modulesInResults={modulesInResults}
        />

        {/* Results table */}
        {/* Pagination — same control as the asset grid and crypto tables */}
        <div className="flex justify-end mb-2">
          <Pagination
            pageIndex={safePageIndex}
            pageCount={pageCount}
            onPageChange={setPageIndex}
            pageSize={pageSize}
            onPageSizeChange={(sz) => { setPageSize(sz); setPageIndex(0); }}
            totalItems={sorted.length}
            itemLabel="assets"
          />
        </div>

        <div className="border border-ink-200 rounded-sm bg-white overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 32 }} />
              <col style={{ width: 260 }} />
              {overallCols.map((m) => (
                <col key={m} style={{ width: 160 }} />
              ))}
              {foundationCols.map((m) => (
                <col key={m} style={{ width: m === "integrated_foundation" ? 160 : 120 }} />
              ))}
              {advancedCols.map((m) => (
                <col key={m} style={{ width: 120 }} />
              ))}
              {hasComparison && <col style={{ width: 120 }} />}
            </colgroup>

            <thead className="bg-ink-50 border-b border-ink-200">
              {/* Group row */}
              <tr className="border-b border-ink-100">
                <th colSpan={2} />
                {overallCols.length > 0 && (
                  <th
                    colSpan={overallCols.length}
                    className="text-center text-[10px] font-semibold uppercase tracking-widest py-1.5
                             bg-ink-100 text-ink-700 border-l border-r border-ink-200"
                  >
                    {moduleLabel("integrated_advanced")}
                  </th>
                )}
                {foundationCols.length > 0 && (
                  <th
                    colSpan={foundationCols.length}
                    className="text-center text-[10px] font-medium uppercase tracking-widest py-1.5
                             bg-accent/5 text-accent border-l border-r border-accent/20"
                  >
                    {TIER_LABEL.foundation}
                  </th>
                )}
                {advancedCols.length > 0 && (
                  <th
                    colSpan={advancedCols.length}
                    className="text-center text-[10px] font-medium uppercase tracking-widest py-1.5
                             text-ink-400"
                  >
                    {TIER_LABEL.advanced}
                  </th>
                )}
                {hasComparison && (
                  <th
                    className="text-center text-[10px] font-medium uppercase tracking-widest py-1.5
                               bg-purple-50 text-purple-700 border-l border-purple-200"
                  >
                    vs Previous
                  </th>
                )}
              </tr>

              {/* Leaf header row */}
              <tr>
                <th />
                <SortableHeader
                  active={sortKey}
                  dir={sortDir}
                  k="asset_name"
                  onClick={() => toggleSort("asset_name")}
                >
                  Asset
                </SortableHeader>
                {allModuleCols.map((m) => (
                  <SortableHeader
                    key={m}
                    active={sortKey}
                    dir={sortDir}
                    k={m}
                    onClick={() => toggleSort(m)}
                  >
                    {moduleLabel(m)}
                  </SortableHeader>
                ))}
                {hasComparison && (
                  <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">
                    Δ {moduleShortLabel(compositeCodeFor(modulesInResults) ?? "integrated_advanced")}
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {pageRows.map((g) => {
                const isOpen = expanded === g.asset_id;
                const prevComposite =
                  compareCompositeByAsset.get(g.asset_id) ?? null;

                return (
                  <FragmentRow key={g.asset_id}>
                    <tr
                      className={`border-b border-ink-100 cursor-pointer transition-colors ${
                        isOpen ? "bg-accent/5" : "hover:bg-ink-50/50"
                      }`}
                      onClick={() => setExpanded(isOpen ? null : g.asset_id)}
                    >
                      <td className="px-2 text-center text-ink-300 select-none">
                        {isOpen ? "▾" : "▸"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-ink-900 truncate">
                          {g.asset_name}
                        </div>
                      </td>
                      {allModuleCols.map((m) => {
                        const r = g.results.find((x) => x.module_code === m);
                        return (
                          <td key={m} className="px-3 py-2">
                            {r ? (
                              <RiskBadge
                                level={r.risk_level}
                                showScore={Number(r.normalized_score)}
                              />
                            ) : (
                              <span className="text-ink-300 text-[10px]">
                                —
                              </span>
                            )}
                          </td>
                        );
                      })}
                      {hasComparison && (
                        <td className="px-3 py-2">
                          <DeltaBadge
                            current={g.composite}
                            previous={prevComposite}
                          />
                        </td>
                      )}
                    </tr>

                    {isOpen && (
                      <tr>
                        <td
                          colSpan={colSpanTotal}
                          className="px-4 py-4 bg-ink-50/40 border-b border-ink-200"
                        >
                          <ExpandedAssetDetail
                            group={g}
                            modulesInResults={modulesInResults}
                            allGroups={groupsWithNames}
                            runId={runId}
                          />
                        </td>
                      </tr>
                    )}
                  </FragmentRow>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

interface SortableHeaderProps {
  k: SortKey;
  active: SortKey;
  dir: SortDir;
  onClick: () => void;
  children: React.ReactNode;
}

function SortableHeader({
  k,
  active,
  dir,
  onClick,
  children,
}: SortableHeaderProps) {
  const isActive = active === k;
  return (
    <th className="text-left text-[11px] font-medium text-ink-500 uppercase tracking-wider px-3 py-2">
      <button
        onClick={onClick}
        className="flex items-center gap-1 hover:text-ink-900"
      >
        {children}
        {isActive && (dir === "asc" ? "▲" : "▼")}
      </button>
    </th>
  );
}

interface ExpandedAssetDetailProps {
  group: AssetGroup;
  modulesInResults: ModuleCode[];
  allGroups: AssetGroup[];
  /** Needed to fetch this asset's detail rows on demand (see below). */
  runId: string | undefined;
}

interface DataRiskAggregation {
  self_score: number | null;
  self_risk_level: RiskLevel | null;
  children_contributions: Array<{
    child_asset_id: string;
    child_score: number | null;
    child_risk_level: RiskLevel;
  }>;
  final_score: number | null;
  final_risk_level: RiskLevel;
  rule: string;
}

function ExpandedAssetDetail({
  group,
  modulesInResults,
  allGroups,
  runId,
}: ExpandedAssetDetailProps) {
  const assetNameById = new Map(
    allGroups.map((g) => [g.asset_id, g.asset_name]),
  );

  // The grid loads the lean payload, which omits parameter_contributions and
  // module_outputs. This panel is the only consumer of those, so it fetches the
  // full rows for THIS asset when opened — one small request per expand,
  // instead of shipping every asset's JSON with the initial page load.
  // Cached by react-query, so re-expanding the same row is free.
  const detailQ = useQuery({
    queryKey: ["run-result-detail", runId, group.asset_id],
    queryFn: () =>
      api.get<RunRow[]>(
        `/runs/${runId}/results?asset_id=${encodeURIComponent(group.asset_id)}`,
      ),
    enabled: !!runId,
    staleTime: 5 * 60 * 1000,
  });

  if (detailQ.isLoading) {
    return <p className="text-xs text-ink-400 italic">Loading detail…</p>;
  }
  if (detailQ.isError) {
    return (
      <p className="text-xs text-risk-critical">
        Couldn't load the breakdown for this asset. The scores above are still
        accurate — collapse and re-open the row to retry.
      </p>
    );
  }

  // Prefer the detail rows (they carry the JSON); fall back to the lean rows.
  const detailRows = detailQ.data ?? [];
  const resultsForDetail = detailRows.length > 0 ? detailRows : group.results;

  return (
    <div className="space-y-4">
      {modulesInResults.map((mod) => {
        const r = resultsForDetail.find((x) => x.module_code === mod);
        if (!r) return null;
        const aggregation =
          mod === "data_risk"
            ? ((
                r.module_outputs as
                  | { aggregation?: DataRiskAggregation }
                  | null
                  | undefined
              )?.aggregation ?? null)
            : null;
        return (
          <div key={mod} className="border border-ink-100 rounded-sm bg-white">
            <div className="flex items-baseline justify-between px-3 py-2 border-b border-ink-100 bg-ink-50/60">
              <div>
                <span className="font-medium text-ink-700">
                  {moduleLabel(mod)}
                </span>
                <span className="ml-2 text-xs text-ink-400 font-mono">
                  score {Number(r.normalized_score).toFixed(1)}
                </span>
              </div>
              <RiskBadge level={r.risk_level} />
            </div>
            {aggregation && (
              <DataRiskAggregationBreakdown
                aggregation={aggregation}
                assetNameById={assetNameById}
              />
            )}
            <ContributionTable contributions={r.parameter_contributions} />
          </div>
        );
      })}
    </div>
  );
}

function DataRiskAggregationBreakdown({
  aggregation,
  assetNameById,
}: {
  aggregation: DataRiskAggregation;
  assetNameById: Map<string, string>;
}) {
  const fmtScore = (s: number | null) =>
    s == null ? "—" : Number(s).toFixed(1);
  return (
    <div className="px-3 py-3 border-b border-ink-100 bg-ink-50/30">
      <div className="text-[10px] text-ink-500 uppercase tracking-wide font-semibold mb-2">
        Data Risk Aggregation
      </div>
      <p className="text-xs text-ink-500 mb-2">
        Final score = <code className="text-[11px]">{aggregation.rule}</code>.
      </p>
      <table className="w-full text-xs">
        <thead className="border-b border-ink-200">
          <tr className="text-left text-ink-500 text-[10px] uppercase tracking-wide">
            <th className="px-2 py-1.5">Source</th>
            <th className="px-2 py-1.5 w-24">Score</th>
            <th className="px-2 py-1.5 w-28">Risk Level</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-ink-100">
            <td className="px-2 py-1.5 text-ink-700">
              <span className="text-ink-500 italic">Self contribution</span>
            </td>
            <td className="px-2 py-1.5 font-mono text-ink-700">
              {fmtScore(aggregation.self_score)}
            </td>
            <td className="px-2 py-1.5">
              <RiskBadge level={aggregation.self_risk_level ?? "NA"} />
            </td>
          </tr>
          {aggregation.children_contributions.map((c) => (
            <tr key={c.child_asset_id} className="border-b border-ink-100">
              <td className="px-2 py-1.5 text-ink-700">
                ↳{" "}
                {assetNameById.get(c.child_asset_id) ?? (
                  <span className="font-mono text-[10px] text-ink-400">
                    {c.child_asset_id.slice(0, 8)}…
                  </span>
                )}
              </td>
              <td className="px-2 py-1.5 font-mono text-ink-700">
                {fmtScore(c.child_score)}
              </td>
              <td className="px-2 py-1.5">
                <RiskBadge level={c.child_risk_level} />
              </td>
            </tr>
          ))}
          <tr className="bg-ink-50/60 font-medium">
            <td className="px-2 py-1.5 text-ink-700">Final (max)</td>
            <td className="px-2 py-1.5 font-mono text-ink-700">
              {fmtScore(aggregation.final_score)}
            </td>
            <td className="px-2 py-1.5">
              <RiskBadge level={aggregation.final_risk_level} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function formatContributionValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  if (typeof value === "object") {
    const v = value as Record<string, unknown>;
    if ("severity" in v && "pervasiveness" in v && "risk" in v) {
      // const parts: string[] = [];
      // if (v.severity) parts.push(`Severity=${v.severity}`);
      // if (v.pervasiveness) parts.push(`Pervasiveness=${v.pervasiveness}`);
      // if (v.likelihood) parts.push(`→ Likelihood=${v.likelihood}`);
      // if (v.impact_level) parts.push(`× Impact=${v.impact_level}`);
      // if (v.risk) parts.push(`→ Risk=${v.risk}`);
      return v.risk as string;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "[unrenderable]";
    }
  }
  return String(value);
}

function ContributionTable({
  contributions,
}: {
  contributions: ParameterContribution[];
}) {
  if (contributions.length === 0) {
    return (
      <div className="px-3 py-3 text-xs text-ink-500 italic">
        No contributions for this module — likely gated out.
      </div>
    );
  }
  return (
    <div className="px-3 py-2">
      <table className="w-full text-xs">
        <thead className="text-ink-400">
          <tr className="text-left">
            <th className="font-medium pb-1.5">Parameter</th>
            <th className="font-medium pb-1.5">Value</th>
            <th className="font-medium pb-1.5 text-right">Weight</th>
            <th className="font-medium pb-1.5 text-right">Contribution</th>
            <th className="font-medium pb-1.5"></th>
          </tr>
        </thead>
        <tbody>
          {contributions.map((c, i) => (
            <tr key={i} className="border-t border-ink-100">
              <td className="py-1.5 pr-3">
                <div className="text-ink-700">{c.parameter_display_name}</div>
                {c.is_inverted && (
                  <div className="text-ink-300 text-[10px]">(inverted)</div>
                )}
                {c.role === "Excluded" && (
                  <div className="text-ink-500 text-[10px] italic">
                    {c.reason_if_excluded}
                  </div>
                )}
              </td>
              <td className="py-1.5 pr-3 font-mono text-ink-700">
                {formatContributionValue(c.value_raw)}
              </td>
              <td className="py-1.5 pr-3 text-right font-mono text-ink-500">
                {c.weight.toFixed(3)}
              </td>
              <td className="py-1.5 pr-3 text-right font-mono text-ink-700">
                {c.contribution_normalized.toFixed(3)}
              </td>
              <td className="py-1.5 text-ink-300 text-[10px]">
                {c.weight_source?.level}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
